// ======================================================================
// File: SendRFQDrawer.jsx
// Module: PPE → Procurement → RFQ
//
// Purpose:
// - Map vendors to RFQ
// - Display mapped vendors
// - Send RFQ in same screen
// - Unified drawer for mapping + sending
// - Enhanced with search + pagination
// ======================================================================

import React, { useEffect, useState } from "react";

import rfqService from "../services/rfqService";
import vendorService from "../../../masters/business_partner/services/businessPartnerService";
import {
  formatDate
} from "../../../../../../utils/formatUtils";

import "../rfq-ui.css";

export default function SendRFQDrawer({
  open,
  onClose,
  onSaved,
  rfqId
}) {

  const [rfqDetail, setRfqDetail] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [loading, setLoading] = useState(false);

  // SEARCH + PAGINATION STATES
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // ==============================================================  
  // INITIALIZE  
  // ==============================================================  
  useEffect(() => {
    if (open && rfqId) {
      loadRFQ();
      loadVendors();
    }
  }, [open, rfqId]);

  // ==============================================================  
  // LOAD RFQ DETAIL  
  // ==============================================================  
  const loadRFQ = async () => {
    try {
      setLoading(true);

      const res = await rfqService.getRFQById(rfqId);

      const data = res?.data?.data || null;

      setRfqDetail(data);

      // Preload already mapped vendors
      const mapped = data?.vendors?.map(v => v.vendor_id) || [];
      setSelectedVendors(mapped);

    } catch (err) {
      console.error("Failed to load RFQ detail", err);
    } finally {
      setLoading(false);
    }
  };

  // ==============================================================  
  // LOAD ALL ACTIVE VENDORS  
  // ==============================================================  
  const loadVendors = async () => {
    try {

      const res = await vendorService.getVendorPartners();

      console.log("RAW VENDORS FROM API:", res);

      setVendors(res || []);

    } catch (err) {
      console.error("Failed to load vendors", err);
    }
  };

  // ==============================================================  
  // HANDLE VENDOR CHECKBOX  
  // ==============================================================  
  const toggleVendor = (vendorId) => {
    if (selectedVendors.includes(vendorId)) {
      setSelectedVendors(
        selectedVendors.filter(id => id !== vendorId)
      );
    } else {
      setSelectedVendors([
        ...selectedVendors,
        vendorId
      ]);
    }
  };

  // ==============================================================  
  // SELECT ALL ON CURRENT PAGE  
  // ==============================================================  
  const toggleSelectAll = (checked, pageVendors) => {
    if (checked) {
      const ids = pageVendors.map(v => v.id);
      const merged = Array.from(new Set([...selectedVendors, ...ids]));
      setSelectedVendors(merged);
    } else {
      const ids = pageVendors.map(v => v.id);
      setSelectedVendors(selectedVendors.filter(id => !ids.includes(id)));
    }
  };

  // ==============================================================  
  // SAVE VENDOR MAPPING  
  // ==============================================================  
  const handleSaveMapping = async () => {

    if (selectedVendors.length === 0) {
      alert("Please select at least one vendor");
      return;
    }

    try {
      setLoading(true);

      await rfqService.mapVendors({
        rfq_id: rfqId,
        vendor_ids: selectedVendors
      });

      alert("Vendors mapped successfully");

      await loadRFQ();

    } catch (err) {
      console.error("Failed to map vendors", err);

      alert(
        err?.response?.data?.message ||
        "Failed to map vendors"
      );

    } finally {
      setLoading(false);
    }
  };

  // ==============================================================  
  // SEND RFQ  
  // ==============================================================  
  const handleSend = async () => {

    if (!rfqDetail?.vendors?.length) {
      alert("No vendors attached to this RFQ");
      return;
    }

    try {
      setLoading(true);

      await rfqService.sendRFQ(rfqId);

      alert("RFQ sent to vendors successfully");

      onSaved?.();

    } catch (err) {
      console.error("Failed to send RFQ", err);

      const message =
        err?.response?.data?.message ||
        "Failed to send RFQ";

      alert(message);

    } finally {
      setLoading(false);
    }
  };

  // ==============================================================  
  // FILTER + PAGINATION LOGIC  
  // ==============================================================  
  const filteredVendors = vendors.filter(v =>
    (v.bp_name || "")
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  const total = filteredVendors.length;
  const totalPages = Math.ceil(total / pageSize);

  const paginated = filteredVendors.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  // ==============================================================  
  // RENDER  
  // ==============================================================  
  if (!open) return null;

  return (
    <div className="pr-drawer open">

      <div className="pr-drawer-header">

        <div>
          <h3>
            {rfqDetail?.rfq_no || "Send RFQ to Vendors"}
          </h3>

          <div className="rfq-header-subtitle">
            Purchase Request :
            {rfqDetail?.pr_no || rfqDetail?.pr_id || "-"}
          </div>
        </div>

        <button
          className="btn-secondary"
          onClick={onClose}
          disabled={loading}
        >
          ✕
        </button>

      </div>

      <div className="pr-drawer-body">

        {loading && (
          <div style={{ marginBottom: "10px" }}>
            Processing...
          </div>
        )}

        {!loading && rfqDetail && (
          <>

            <div className="rfq-info-card">

              <div className="rfq-info-row">
                <label>RFQ Date</label>
                <span>{formatDate(rfqDetail.rfq_date)}</span>
              </div>

              <div className="rfq-info-row">
                <label>Quote Due Date</label>
                <span>{formatDate(rfqDetail.quote_due_date)}</span>
              </div>

              <div className="rfq-info-row">
                <label>Validity Date</label>
                <span>
                  {rfqDetail.validity_date
                    ? formatDate(rfqDetail.validity_date)
                    : "-"}
                </span>
              </div>

              <div className="rfq-info-row">
                <label>Status</label>

                <span
                  className={`rfq-status-badge rfq-status-${(
                    rfqDetail.status_code || "draft"
                  ).toLowerCase()}`}
                >
                  {rfqDetail.status_name || rfqDetail.status_code}
                </span>
              </div>

            </div>

            <div className="section-header">
              <h4>Select Vendors</h4>
            </div>

            {/* SEARCH BOX */}
            <input
              type="text"
              className="pr-input"
              placeholder="Search vendor..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              style={{ marginBottom: "10px" }}
            />

            <div className="rfq-table-wrapper">
              <table className="rfq-table">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          toggleSelectAll(e.target.checked, paginated)
                        }
                      />
                    </th>
                    <th>Vendor Name</th>
                  </tr>
                </thead>

                <tbody>
                  {paginated.map(v => (
                    <tr key={v.id}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedVendors.includes(v.id)}
                          onChange={() => toggleVendor(v.id)}
                        />
                      </td>

                      <td>{v.bp_name}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* PAGINATION CONTROLS */}
            <div style={{ marginTop: "10px", display: "flex", gap: "10px" }}>

              <button
                className="btn-secondary"
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
              >
                Prev
              </button>

              <span>
                Page {page} of {totalPages || 1}
              </span>

              <button
                className="btn-secondary"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </button>

              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
              >
                <option value="10">10</option>
                <option value="20">20</option>
                <option value="50">50</option>
              </select>

            </div>

            <div style={{ marginTop: "10px" }}>
              <button
                className="btn-primary"
                onClick={handleSaveMapping}
                disabled={loading}
              >
                Save Vendor Mapping
              </button>
            </div>

            <div className="section-header" style={{ marginTop: "20px" }}>
              <h4>Mapped Vendors</h4>
            </div>

            <div className="rfq-table-wrapper">
              <table className="rfq-table">
                <thead>
                  <tr>
                    <th>Vendor</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {(!rfqDetail.vendors || rfqDetail.vendors.length === 0) && (
                    <tr>
                      <td colSpan="2">No vendors attached</td>
                    </tr>
                  )}

                  {rfqDetail.vendors?.map((v) => {

                  const vendorInfo = vendors.find(x => x.id === v.vendor_id);

                  return (
                    <tr key={v.id}>
                      <td>
                        {vendorInfo?.bp_name ||
                        vendorInfo?.name ||
                        v.vendor_name ||
                        v.vendor_id}
                      </td>
                      <td>{v.response_status || "PENDING"}</td>
                    </tr>
                  );

                })}

                </tbody>
              </table>
            </div>

          </>
        )}

      </div>

      <div className="pr-drawer-footer">
        <button className="btn-secondary" onClick={onClose}>
          Cancel
        </button>

        <button className="btn-primary" onClick={handleSend}>
          Send RFQ
        </button>
      </div>

    </div>
  );
}
